/**
 * "Nothing else is waiting on this booking — mark it Completed?"
 *
 * WHY THIS EXISTS. The nightly AutoCompleteService deliberately leaves a trip
 * open for AUTO_COMPLETE_SETTLE_HOURS (48) after it ends so claims can still
 * be filed and reviewed, and a pending claim holds it open past that
 * (TripClaimsPendingError). So dealing with a booking's LAST claim used to
 * leave it sitting until a nightly run at least two days out, for no reason
 * anybody wanted. The offer closes it there and then.
 *
 * WHAT "NOTHING ELSE OUTSTANDING" MEANS. It is not re-derived here. It is the
 * API's own answer — the booking's `trip_claims.blocking`, which the bookings
 * endpoint puts on every booking straight out of TripClaimsService — read
 * FRESH off the booking after the decision has been committed.
 *
 * `blocking`, not `total`, and that is the whole point of reading the server's
 * answer instead of counting rows on screen: a claim filed before the API's
 * TRIP_CLAIMS_BLOCK_FROM is listed and counted for staff but holds nothing
 * back, and the server would complete the booking regardless of it. Counting
 * what is on screen would refuse to offer a close the server would grant, and
 * the two definitions would drift the first time either moved.
 */
import { blockingClaimsOf } from './tripClaims';

/**
 * The only status a manual completion is meaningful from.
 *
 * The statuses the dashboard itself writes (OrderInputModal's list) are
 * Completed, Confirmed, Cancelled, Request, Driver No-Show and Passenger
 * No-Show; the ingestion pipeline adds Pending Ingestion and Ingestion
 * Failed. Of those, only Confirmed is a trip that ran and has not been
 * closed:
 *
 *   - Completed — already there; the API's own guard skips the transition.
 *   - Cancelled, Driver No-Show, Passenger No-Show — the booking finished
 *     another way. Completing one would overwrite that record and fire the
 *     post-trip customer email for a trip that never happened.
 *   - Request — never confirmed, so it never ran.
 *   - Pending Ingestion, Ingestion Failed — the pipeline owns the row, and
 *     the dashboard's own rule (bookingStatus.isSystemManagedStatus) is that
 *     its status is not changed by hand.
 *
 * An unknown status is not offered either: this asks whether to volunteer an
 * action, so silence is the safe answer.
 */
export const COMPLETABLE_STATUS = 'Confirmed';

/**
 * Whether the booking has an operator — the API's AutoCompleteService
 * `hasOperatorAssigned`, which the nightly job requires before it completes
 * anything: the legacy lead in `operator_id`, or any entry in `operators[]`.
 *
 * Reading both matters rather than just `operator_id`: an experience run by a
 * guide never syncs one (only driver entries do), so a gate on `operator_id`
 * alone would silently skip guide-run experiences.
 */
export const hasOperatorAssigned = (order) => Boolean(order?.operator_id)
  || (Array.isArray(order?.operators) && order.operators.length > 0);

/**
 * Whether to offer to complete this booking, given the booking as the API
 * just returned it. Everything it reads comes off that one response.
 */
export const canOfferCompletion = (order) => Boolean(order)
  && order.status === COMPLETABLE_STATUS
  && hasOperatorAssigned(order)
  && blockingClaimsOf(order).total === 0;

export default canOfferCompletion;
